import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatPrice } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useCMS, useCText } from "../lib/cms";
import VinylCard from "../components/VinylCard";
import NewsletterForm from "../components/NewsletterForm";
import { ArticleCard } from "./Articles";

const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1483412033650-1015ddeb83d1?auto=format&fit=crop&w=2400&q=80";

function resolveImage(url, fallback) {
  if (!url) return fallback;
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL || ""}${url}`;
}

export default function Home() {
  const { t, lang } = useI18n();
  const intro = useCText("home.intro", "home.intro");
  const latestTitle = useCText("home.latest.title", "home.latest.title");
  const articlesTitle = useCText("home.articles.title", "home.articles.title");
  const newsletterTitle = useCText("home.newsletter.title", "home.newsletter.title");
  const newsletterCopy = useCText("home.newsletter.copy", "home.newsletter.copy");
  // Read the CMS value directly: an empty field must fall back to HERO_FALLBACK, not to the key name.
  const { blocks } = useCMS();
  const heroImage = blocks?.["image.hero.url"]?.es || "";
  const featuredTitle = useCText("featured.title", "featured.title");
  const featuredNote = useCText("featured.note", "featured.note");
  const banner = resolveImage(heroImage, HERO_FALLBACK);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [pick, setPick] = useState(null);
  const [loading, setLoading] = useState(true);

  const [articles, setArticles] = useState([]);
  useEffect(() => {
    api.get("/articles").then((r) => setArticles((r.data.items || []).slice(0, 3))).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/listings/latest?limit=12"),
      api.get("/listings?per_page=1"),
      api.get("/home/featured?per_group=6&groups=3&by=style"),
      api.get("/featured-pick"),
    ])
      .then(([latest, all, feat, fp]) => {
        setItems(latest.data);
        setTotal(all.data?.pagination?.total ?? null);
        setFeatured(feat.data);
        setPick(fp.data?.item || null);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO BANNER con foto + intro encima */}
      <section className="relative border-b border-border overflow-hidden" data-testid="home-hero">
        <div
          className="relative h-[42vh] min-h-[300px] max-h-[460px]"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(15,15,15,0.10) 0%, rgba(15,15,15,0.30) 55%, rgba(15,15,15,0.70) 100%), url(${banner})`,
            backgroundSize: "cover",
            backgroundPosition: "center 40%",
          }}
        >
          <div className="absolute inset-0 max-w-[1600px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-8 md:pb-12">
            <p className="text-white/95 text-base md:text-lg max-w-2xl leading-relaxed" data-testid="home-intro">
              {intro}
              {total !== null && (
                <span className="text-white/65"> {t("home.intro.suffix").replace("{n}", total)}</span>
              )}
            </p>
            <div className="flex flex-wrap gap-2.5 mt-6">
              <Link
                to="/catalogo"
                className="bg-foreground text-background px-5 py-2.5 text-sm font-medium hover:bg-[hsl(var(--accent))] transition-colors"
                data-testid="home-cta-shop"
              >
                {t("home.cta.shop")} →
              </Link>
              <Link
                to="/newsletter"
                className="border border-white/80 text-white px-5 py-2.5 text-sm font-medium hover:bg-white hover:text-foreground transition-colors"
                data-testid="home-cta-newsletter"
              >
                {t("nav.subscribe")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Disco de la semana */}
      {pick && (
        <section
          className="border-b border-border"
          data-testid="home-featured-pick"
        >
          <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-12 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
            <Link to={`/catalogo/${pick.slug}`} className="md:col-span-5 block aspect-square bg-[hsl(var(--muted))] overflow-hidden group">
              <img
                src={pick.cover_image || pick.thumb}
                alt={`${pick.artist} — ${pick.title}`}
                className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-[1.02]"
                data-testid="home-featured-pick-cover"
              />
            </Link>
            <div className="md:col-span-7 flex flex-col gap-4">
              <p className="text-xs tracking-[0.2em] uppercase text-[hsl(var(--accent))]" data-testid="home-featured-pick-tag">
                {featuredTitle}
              </p>
              <h2 className="text-2xl md:text-4xl font-medium leading-tight" data-testid="home-featured-pick-title">
                <span className="text-muted-foreground">{pick.artist}</span>
                <br />
                <span>{pick.title}</span>
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-xl whitespace-pre-line">
                {featuredNote}
              </p>
              <p className="text-xs text-muted-foreground">
                {[pick.label, pick.catno, pick.year, (pick.format_details || []).join(" / ")].filter(Boolean).join(" · ")}
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-2">
                <span className="text-xl font-medium tabular-nums">
                  {formatPrice(pick.price, pick.currency)}
                </span>
                <Link
                  to={`/catalogo/${pick.slug}`}
                  className="bg-foreground text-background px-5 py-2.5 text-sm font-medium hover:bg-[hsl(var(--accent))] transition-colors"
                  data-testid="home-featured-pick-cta"
                >
                  Ver disco →
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Últimos ingresos */}
      <section className="max-w-[1600px] mx-auto px-5 md:px-10 pt-10 md:pt-14 pb-12 md:pb-16">
        <div className="flex items-baseline justify-between mb-6 md:mb-8">
          <h2 className="text-sm font-medium text-foreground" data-testid="home-latest-title">
            {latestTitle}
          </h2>
          <Link
            to="/catalogo"
            className="text-sm text-muted-foreground hover:text-foreground transition-opacity"
            data-testid="home-link-catalog"
          >
            {t("home.latest.viewAll")} →
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-8 md:gap-x-5 md:gap-y-10">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="aspect-square bg-[hsl(var(--muted))] animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground text-sm" data-testid="home-empty">
            {t("home.empty")}
          </div>
        ) : (
          <div
            className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-8 md:gap-x-5 md:gap-y-10"
            data-testid="home-grid"
          >
            {items.map((it) => (
              <VinylCard key={it.listing_id} item={it} />
            ))}
          </div>
        )}

        <div className="mt-8 md:mt-10">
          <Link
            to="/catalogo"
            className="inline-block text-sm text-foreground vmr-link-underline"
            data-testid="home-cta-shop-bottom"
          >
            {t("home.viewFullCatalog")} →
          </Link>
        </div>
      </section>

      {/* Filas por subgénero — los styles con más discos */}
      {featured?.rows?.map((group) => (
        <section
          key={group.title}
          className="max-w-[1600px] mx-auto px-5 md:px-10 py-10 md:py-14 border-t border-border"
          data-testid={`home-genre-row-${group.title}`}
        >
          <div className="flex items-baseline justify-between mb-6 md:mb-8">
            <h2 className="text-sm font-medium" data-testid={`home-genre-title-${group.title}`}>
              {group.title}
            </h2>
            <Link
              to={`/catalogo?${group.filter_param || "style"}=${encodeURIComponent(group.title)}`}
              className="text-sm text-muted-foreground hover:text-foreground transition-opacity"
              data-testid={`home-genre-view-${group.title}`}
            >
              {t("home.viewGenre")} →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-8 md:gap-x-5 md:gap-y-10">
            {group.items.map((it) => (
              <VinylCard key={it.listing_id} item={it} />
            ))}
          </div>
        </section>
      ))}

      {/* Artículos recomendados */}
      {articles.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-5 md:px-10 pt-4 pb-14 md:pb-20" data-testid="home-articles">
          <div className="flex items-baseline justify-between mb-6 md:mb-8">
            <h2 className="text-lg md:text-xl font-medium">{articlesTitle}</h2>
            <Link to="/articulos" className="text-xs text-muted-foreground hover:text-foreground">{lang === "en" ? "See all →" : "Ver todos →"}</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {articles.map((a) => <ArticleCard key={a.id} article={a} lang={lang} />)}
          </div>
        </section>
      )}

      {/* Newsletter */}
      <section className="border-t border-border" data-testid="home-newsletter">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-14 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start">
            <div className="md:col-span-5">
              <h2 className="text-xl md:text-2xl font-medium text-foreground mb-2">
                {newsletterTitle}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-md">
                {newsletterCopy}
              </p>
            </div>
            <div className="md:col-span-7">
              <NewsletterForm context="home" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

